// Job Order detail — requisition + rates + client/recruiter + submissions + placements.
import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StaffingSubmissionsPanel } from "@/components/staffing-submissions-panel";
import { StaffingPlacementsPanel } from "@/components/staffing-placements-panel";
import { Trash2 } from "lucide-react";

function KV({ k, v }: { k: string; v: ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-right text-sm">{v}</span>
    </div>
  );
}
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : null);
const money = (v: any) => (v == null ? null : `$${Number(v).toLocaleString()}`);

type Tab = "submissions" | "placements";

export default function JobOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("submissions");

  const { data, isLoading } = useQuery({
    queryKey: ["/job-orders", id],
    queryFn: async () => (await api.get(`/job-orders/${id}`)).data,
    enabled: !!id,
  });

  async function del() {
    if (!data || !confirm(`Delete job order "${data.title}"?`)) return;
    await api.delete(`/job-orders/${id}`);
    qc.invalidateQueries({ queryKey: ["/job-orders"] });
    navigate("/job-orders");
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;
  const j = data;
  const ratesHidden = j.billRate == null && j.payRate == null && j.marginPct == null;

  return (
    <>
      <PageHeader
        title={j.title}
        description={`${j.status}${j.clientAccount?.name ? ` · ${j.clientAccount.name}` : ""}`}
        actions={<Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>}
      />
      <div className="space-y-4 p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Requisition</CardTitle></CardHeader>
            <CardContent>
              <KV k="Status" v={<Badge variant="secondary">{j.status}</Badge>} />
              <KV k="Location" v={`${j.locationType}${j.location ? ` · ${j.location}` : ""}`} />
              <KV k="Skills" v={j.skillsRequired?.length ? j.skillsRequired.join(", ") : null} />
              <KV k="Visa req" v={j.visaRequirement} />
              <KV k="Government" v={j.isGovernment ? "Yes" : null} />
              <KV k="Clearance" v={j.clearanceLevel} />
              <KV k="Target fill" v={fmtDate(j.targetFillDate)} />
              <KV k="Entity" v={j.entity} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Rates</CardTitle></CardHeader>
            <CardContent>
              <KV k="Bill rate" v={money(j.billRate)} />
              <KV k="Pay rate" v={money(j.payRate)} />
              <KV k="Margin %" v={j.marginPct != null ? `${j.marginPct}%` : null} />
              {ratesHidden && <p className="text-xs text-muted-foreground">Rates hidden or not set.</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Client &amp; owner</CardTitle></CardHeader>
            <CardContent>
              <KV k="Client" v={j.clientAccount ? <Link className="hover:underline" to={`/accounts/${j.clientAccount.id}`}>{j.clientAccount.name}</Link> : null} />
              <KV k="Recruiter" v={j.recruiter?.name} />
            </CardContent>
          </Card>
        </div>
        <Card>
          <div className="flex gap-1 border-b px-2 pt-2">
            {(["submissions", "placements"] as Tab[]).map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`rounded-t px-3 py-1.5 text-sm ${tab === t ? "border-b-2 border-primary font-medium text-primary" : "text-muted-foreground"}`}>
                {t === "submissions" ? "Submissions" : "Placements"}
              </button>
            ))}
          </div>
          <CardContent className="pt-4">
            {tab === "submissions" && <StaffingSubmissionsPanel jobOrderId={j.id} />}
            {tab === "placements" && <StaffingPlacementsPanel jobOrderId={j.id} />}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
